import { useContext, useState } from "react"

import { 
  ContactSequence, 
  PasswordResetSuccessPlaceholder as PasswordResetSuccess, 
  SimpleIconList, 
  SuccessPlaceholder 
} from "@components"

import { 
  NOTIFICATION_TYPE_ID, 
  NOTIFICATION_STATUS_ID, 
  NOTIFICATION_RECIPIENT_TYPE_ID 
} from "@constants"

import { NotificationContext } from "@context"
import { CREATE_NOTIFICATION_MUTATION } from "@graphql"
import { addNotificationToCache, generatePassword, getGraphQLErrorMessage, mySqlToLocal } from "@helpers"
import { useActivateAccount } from '@hooks'
import { useMutation } from "@apollo/client"
import { Button, Link } from "@heroui/react"

import { 
  AcademicCapIcon, 
  AtSymbolIcon, 
  CheckBadgeIcon, 
  LockOpenIcon,
  LockClosedIcon,
  PaperAirplaneIcon, 
  PhoneIcon, 
  ChevronRightIcon,
  ShieldCheckIcon, 
  UserIcon, 
  XCircleIcon,
} from "@heroicons/react/24/outline"

import { 
  CheckBadgeIcon as CheckBadgeIconSolid, 
  CheckCircleIcon as CheckCircleIconSolid, 
  PaperAirplaneIcon as PaperAirplaneIconSolid, 
  ShieldCheckIcon as ShieldCheckIconSolid 
} from "@heroicons/react/24/solid"

import { uniq } from "lodash"
import PropTypes from 'prop-types'

const NEWLINE = "\r\n\r\n"

const Details = ({ 
  event, 
  handlePollForSendingEmails, 
  invitation, 
  isReadOnly 
}) => {
  const [hasAccountActivated, setHasAccountActivated] = useState(false)
  const [isActivatingAccount, setIsActivatingAccount] = useState(false)
  const [isSendingToEmailQueue, setIsSendingToEmailQueue] = useState(false)
  const [isEmailQueued, setIsEmailQueued] = useState(false)
  const [password, setPassword] = useState('')
  const {notify} = useContext(NotificationContext)
  
  const { activate } = useActivateAccount()

  const [createNotification] = useMutation(CREATE_NOTIFICATION_MUTATION, {
    variables: {
      input: {
        event_id: event.id,
        title: `${invitation.guardian}`,
        description: "Invitation email sent to selected guardians.",
        type_id: NOTIFICATION_TYPE_ID,
        status_id: NOTIFICATION_STATUS_ID,
        recipient_type_id: NOTIFICATION_RECIPIENT_TYPE_ID,
        user_ids: [invitation.user_id],
      },
    },

    onCompleted: () => {
      setIsSendingToEmailQueue(false)
      setIsEmailQueued(true)
      handlePollForSendingEmails(true)
    },

    onError: (errors) => {
      notify({ title: "Error", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while creating the notification."), open: true })
      setIsSendingToEmailQueue(false)
      setIsEmailQueued(false)
    },

    update: (cache, { data: { createNotification } }) => addNotificationToCache(cache, createNotification, event.id)
  })
      
  const getActivationMailToUrl = () => 
    `mailto:${invitation.email}?subject=${encodeURIComponent(getEmailSubject())}&body=${encodeURIComponent(getCustomActivationEmailBody())}`

  const getActivationUrl = () => `https://parents.events.schoolbooking.com/activate-account?code=${invitation.activation_code}&email=${invitation.email}&client_id=${event.client_id}`
  
  const getCustomActivationEmailBody = () => {
    return [
      `You have been invited to parents evening: ${event.title.trim()}.`,
      `Click or paste the URL below into your browser to activate your account:`,
      `${getActivationUrl()}`
    ].join(NEWLINE)
  }
  
  const getCustomInvitationEmailBody = () => {
    return [
      `You have been invited to parents evening: ${event.title.trim()}.`,
      `Event Description: ${event.description}`,
      `Click or paste the URL below into your browser to view this event:`,
      `${getInvitationUrl()}`
    ].join(NEWLINE)
  }

  const getEmailSubject = () => `Invitation to ${event.title}`
  
  const getInvitationUrl = () => `https://parents.events.schoolbooking.com/parentsmeeting/${event.id}`
  
  const getInvitationMailToUrl = () => 
    `mailto:${invitation.email}?subject=${encodeURIComponent(getEmailSubject())}&body=${encodeURIComponent(getCustomInvitationEmailBody())}`
  
  const handleActivateAccount = () => {
    const newPassword = generatePassword(8)
    setPassword(newPassword)
    setIsActivatingAccount(true)

    activate({
      password: newPassword,
      userId: invitation.user_id,
      onSuccess: (data) => setHasAccountActivated(true),
      onError: (errors) => notify({ title: "Error Activating Account", type: "danger", content: getGraphQLErrorMessage(errors, "An error occurred while activating the account."), open: true }),
      update: (cache, adminActivateAccount) => {
        cache.modify({
          id: cache.identify({ __typename: 'InvitationMatrixItem', id: invitation.id }),
          fields: {
            activated_at: () => adminActivateAccount.activated_at,
          }
        })
      }
    }).then(() => setIsActivatingAccount(false))
  }

  const handleCopyToClipboard = (text) => {
    navigator.clipboard.writeText(text)
  }

  const handleQueueInvitation = () => {
    setIsSendingToEmailQueue(true)
    createNotification()
  }

  const phoneNumbers = uniq([
    invitation?.user_mobile ? invitation.user_mobile.replaceAll(" ", "") : "",
    invitation?.guardian_telephone ? invitation.guardian_telephone.replaceAll(" ", "") : ""
  ].filter(phoneNumber => phoneNumber.length > 5)).join(" / ")

  const detailsListItems = [
    { icon: <AcademicCapIcon className="text-indigo-800 dark:text-content2-foreground" />, caption: `${invitation.student}`, show: true },
    { icon: <UserIcon className="text-indigo-800 dark:text-content2-foreground" />, caption: `${invitation.guardian}`, show: invitation.status === "Matched" },
    { icon: <AtSymbolIcon className="text-indigo-800 dark:text-content2-foreground" />, caption: `${invitation.email}`, show: invitation.status === "Matched" },
    { icon: <PhoneIcon className="text-indigo-800 dark:text-content2-foreground" />, caption: `${phoneNumbers}`, show: phoneNumbers.length > 0 },
  ]

  const invitationStatus = [
    { icon: <CheckBadgeIconSolid className="text-green-400" />, caption: `Student is matched to a guardian`, show: invitation.status === "Matched"},
    { icon: <CheckBadgeIcon className="text-gray-400" />, caption: `Student Could not be Matched to a Guardian`, show: invitation.status !== "Matched"},
    { icon: <ShieldCheckIconSolid className="text-green-400" />, caption: `The guardian is a legal guardian`, show: invitation.legal_guardian },
    { icon: <ShieldCheckIcon className="text-gray-400" />, caption: `The guardian is NOT a legal guardian`, show: !invitation.legal_guardian },
    { icon: <ContactSequence priority={invitation.contact_sequence} size="md" />, caption: `Contact Sequence: ${invitation.contact_sequence}`, show: true },
    { icon: <PaperAirplaneIconSolid className="text-green-400" />, caption: `Last invitation email sent on ${mySqlToLocal(invitation.last_sent_at).format("DD/MM/YYYY HH:mm")}`, show: invitation.last_sent_at !== null },
    { icon: <PaperAirplaneIcon className="text-gray-400" />, caption: `No invitation email sent yet`, show: invitation.last_sent_at === null },
  ]

  const guardianStatus = [
    { icon: <CheckCircleIconSolid className="text-green-400" />, caption: `Account was activated on ${mySqlToLocal(invitation.activated_at).format("DD/MM/YYYY HH:mm")}`, show: invitation.activated_at !== null },
    { icon: <XCircleIcon className="text-gray-400" />, caption: `This account has not been activated yet`, show: invitation.activated_at === null },
    { icon: <LockOpenIcon className="text-green-400" />, caption: `Last logged in ${mySqlToLocal(invitation.last_active_at).format("DD/MM/YYYY HH:mm")}`, show: invitation.last_active_at !== null },
    { icon: <LockClosedIcon className="text-gray-400" />, caption: `Never logged in`, show: invitation.last_active_at === null },
  ]

  if (isEmailQueued) {
    return (
      <SuccessPlaceholder 
        title="Invitation Email Sent"
        message={`A invitation email for ${invitation.email} has been sent to the email queue and will be sent shortly.`}
      />
    )
  }
  
  if (hasAccountActivated) {
    return (
      <PasswordResetSuccess
        handleCopyToClipboard={(text) => handleCopyToClipboard(text)}
        title="Account Activated"
        message="This account has been activated. You can now login with the details below."
        password={password} 
      />
    )
  }

  return (
    <div>
      <div id="contact-details">
        <h2 className="text-lg font-semibold">Contact Details</h2>
        <SimpleIconList iconSize="md" items={detailsListItems.filter(item => item.show)} />    
      </div>
      
      <div id="invitation-status">
        <h2 className="mt-6 text-lg font-semibold">Invitation Status</h2> 
        <SimpleIconList iconSize="md" items={invitationStatus.filter(item => item.show)} />     
      </div>

      <div id="guardian-status">
        <h2 className="mt-6 text-lg font-semibold">Guardian Account Status</h2> 
        <SimpleIconList iconSize="md" items={guardianStatus.filter(item => item.show)} /> 
      </div>  

      {!isReadOnly && (
        <div id="actions" className="mt-6">
          {invitation.status === "Matched" && !invitation.last_email_queued && (
            <Button
              className="mb-2 font-semibold text-white" 
              color="success" 
              isLoading={isSendingToEmailQueue}
              onPress={() => handleQueueInvitation()}
              fullWidth
            >
              {invitation.last_sent_at !== null ? "Resend" : "Send"} Invitation Email
            </Button>
          )}

          {invitation.status === "Matched" && invitation.activated_at === null && (
            <Button className="mb-2" as={Link} href={`${getActivationMailToUrl()}`} fullWidth>Send Custom Activation Email</Button>
          )}

          {invitation.status === "Matched" && invitation.activated_at === null && (
            <Button color="secondary" fullWidth onPress={() => handleActivateAccount()} isLoading={isActivatingAccount}>
              Manually Activate
            </Button>
          )}

          {invitation.status === "Matched" && invitation.activated_at !== null && (
            <Button as={Link} href={`${getInvitationMailToUrl()}`} fullWidth>Send Custom Invitation Email 
              <ChevronRightIcon className="w-4 h-4" />
            </Button>
          )}
        </div>
      )}
    </div>
  )
}

Details.propTypes = {
  event: PropTypes.object.isRequired,
  handlePollForSendingEmails: PropTypes.func.isRequired,
  invitation: PropTypes.object.isRequired,
  isReadOnly: PropTypes.bool
}

export default Details